import {useState, useMemo, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Pencil, Trash2, Check, Minus, Settings} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {SmallText} from '@/components/shared/SmallText'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {Input} from '@/components/ui/input'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import type {Permission, RoleUpdateRequest, RolePermissionsUpdateRequest} from '@/types/roles'
import {PageLayout} from '@/components/shared/PageLayout'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {CardSection} from '@/components/shared/CardSection'
import {DetailField} from '@/components/shared/DetailField'
import {MutedText} from '@/components/shared/MutedText'
import {CodeText} from '@/components/shared/CodeText'

const ACTION_ORDER: readonly string[] = ['view', 'create', 'update', 'delete']

interface PermissionsMatrixProps {
    rolePermissions: Permission[]
    allPermissions: Permission[]
    isEditing: boolean
    selectedPermissionIds: Set<string>
    onTogglePermission: (permissionId: string) => void
}

function PermissionsMatrix({
    allPermissions,
    isEditing,
    selectedPermissionIds,
    onTogglePermission,
}: PermissionsMatrixProps) {
    const i18n = useI18n()
    const [resourceFilter, setResourceFilter] = useState('')

    // Build a matrix: rows = resources, columns = actions from ALL permissions
    const {resources, actions, permissionMap} = useMemo(() => {
        const resourceSet = new Set<string>()
        const actionSet = new Set<string>()
        const permMap = new Map<string, Permission>() // permissionString -> Permission

        // Get all resources and actions from ALL available permissions
        allPermissions.forEach((perm) => {
            const [resource, action] = perm.permissionString.split(':')
            resourceSet.add(resource)
            actionSet.add(action)
            permMap.set(perm.permissionString, perm)
        })

        // Sort actions in logical order
        const sortedActions = Array.from(actionSet).sort((a, b) => {
            const aIdx = ACTION_ORDER.indexOf(a)
            const bIdx = ACTION_ORDER.indexOf(b)
            if (aIdx === -1 && bIdx === -1) return a.localeCompare(b)
            if (aIdx === -1) return 1
            if (bIdx === -1) return -1
            return aIdx - bIdx
        })

        return {
            resources: Array.from(resourceSet).sort(),
            actions: sortedActions,
            permissionMap: permMap,
        }
    }, [allPermissions])

    const getPermission = (resource: string, action: string): Permission | undefined => {
        return permissionMap.get(`${resource}:${action}`)
    }

    const isSelected = (permissionId: string) => {
        return selectedPermissionIds.has(permissionId)
    }

    const selectedCount = selectedPermissionIds.size

    return (
        <Card>
            <CardHeader>
                <CardTitle data-testid="card-title-permissions">
                    {i18n.common.permissions}
                    {isEditing && <Badge variant="secondary">{i18n.roles.editing}</Badge>}
                </CardTitle>
                <CardDescription>
                    {i18n.roles.permissionsAssigned({ selected: String(selectedCount), total: String(allPermissions.length) })}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
                <Input
                    placeholder={i18n.common.filterByResource}
                    value={resourceFilter}
                    onChange={(e) => setResourceFilter(e.target.value)}
                    className="max-w-xs"
                    data-testid="input-filter-permissions"
                />
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead data-testid="col-resource">{i18n.roles.resource}</TableHead>
                            {actions.map((action) => (
                                <TableHead key={action} className="text-center w-24" data-testid={`col-action-${action}`}>
                                    <SmallText className="capitalize">{action}</SmallText>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {resources.filter((r) => r.includes(resourceFilter.toLowerCase())).map((resource) => (
                            <TableRow key={resource}>
                                <TableCell className="font-medium">{resource}</TableCell>
                                {actions.map((action) => {
                                    const permission = getPermission(resource, action)
                                    if (!permission) {
                                        return <TableCell key={action} className="text-center"/>
                                    }

                                    const selected = isSelected(permission.id)

                                    if (isEditing) {
                                        return (
                                            <TableCell key={action} className="text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => onTogglePermission(permission.id)}
                                                    className="cursor-pointer"
                                                />
                                            </TableCell>
                                        )
                                    }

                                    return (
                                        <TableCell key={action} className="text-center">
                                            {selected ? (
                                                <Icon icon={Check} className="mx-auto text-status-success"/>
                                            ) : (
                                                <Icon icon={Minus} className="mx-auto text-muted-foreground/30"/>
                                            )}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    )
}

export function RoleDetail() {
    const services = useServices()
    const i18n = useI18n()
    const {id} = useParams<{ id: string }>()
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const editDialog = useDialog()
    const deleteDialog = useDialog()
    const [formData, setFormData] = useState({name: '', description: ''})
    const [isEditingPermissions, setIsEditingPermissions] = useState(false)
    const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set())

    const canUpdate = hasPermission(permissions.roles.update)
    const canDelete = hasPermission(permissions.roles.delete)


    const ROLE_KEY = 'role-detail' as const
    const PERMISSIONS_KEY = 'permission-list' as const


    // Fetch role
    const {data: role, isLoading, error} = useQuery({
        queryKey: [ROLE_KEY, id],
        queryFn: () => services.roles.detail(id!),
        enabled: !!id,
    })

    // Fetch all permissions for the matrix display
    const {data: allPermissions = []} = useQuery({
        queryKey: [PERMISSIONS_KEY],
        queryFn: () => services.roles.permissions(),
    })

    // Initialize selected permissions when role data loads
    useEffect(() => {
        if (role) {
            setSelectedPermissionIds(new Set(role.permissions.map(p => p.id)))
        }
    }, [role])

    // Update role mutation
    const updateMutation = useEntityMutation({
        mutationFn: (data: RoleUpdateRequest) =>
            services.roles.update(id!, data),
        invalidate: [
            [ROLE_KEY, id],
            ['role-list'],
        ],
        successMessage: i18n.roles.roleUpdated,
        closeDialog: editDialog.close,
    })

    // Update permissions mutation
    const updatePermissionsMutation = useEntityMutation({
        mutationFn: (data: RolePermissionsUpdateRequest) =>
            services.roles.updatePermissions(id!, data),
        invalidate: [
            [ROLE_KEY, id],
            ['role-list'],
        ],
        successMessage: i18n.roles.permissionsSaved,
        closeDialog: () => setIsEditingPermissions(false),
    })

    // Delete role mutation
    const deleteMutation = useEntityMutation({
        mutationFn: () => services.roles.remove(id!),
        invalidate: ['role-list'],
        successMessage: i18n.roles.roleDeleted,
        onSuccess: () => {
            navigate(routes.admin.roles.list())
        },
    })

    const openEditDialog = () => {
        if (role) {
            setFormData({name: role.name, description: role.description || ''})
            editDialog.open()
        }
    }

    const handleUpdate = () => {
        if (!role) return
        updateMutation.mutate({
            name: formData.name,
            description: formData.description || undefined,
            version: role.version,
        })
    }

    const handleDelete = () => {
        deleteMutation.mutate()
    }

    const handleTogglePermission = (permissionId: string) => {
        setSelectedPermissionIds(prev => {
            const next = new Set(prev)
            if (next.has(permissionId)) {
                next.delete(permissionId)
            } else {
                next.add(permissionId)
            }
            return next
        })
    }

    const handleStartEditingPermissions = () => {
        if (role) {
            setSelectedPermissionIds(new Set(role.permissions.map(p => p.id)))
            setIsEditingPermissions(true)
        }
    }

    const handleCancelEditingPermissions = () => {
        if (role) {
            setSelectedPermissionIds(new Set(role.permissions.map(p => p.id)))
        }
        setIsEditingPermissions(false)
    }

    const handleSavePermissions = () => {
        if (!role) return
        updatePermissionsMutation.mutate({
            permissionIds: Array.from(selectedPermissionIds),
            version: role.version,
        })
    }

    return (
        <PageLayout
            variant="detail"
            title={role?.name ?? i18n.roles.detailPageTitle}
            icon={CONCEPT_ICON.adminRole}
            description={role?.systemRole ? i18n.roles.system : role ? i18n.roles.custom : undefined}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(role, 'ROLE') : undefined)}
            backTo={routes.admin.roles.list()}
            data-testid="role-detail-page"
            actions={role && (canUpdate || canDelete) ? (
                <>
                    {canUpdate && (
                        <Button data-testid="btn-edit-role" variant="outline" size="sm" onClick={openEditDialog}>
                            <Icon icon={Pencil} className="mr-2"/>
                            {i18n.common.edit}
                        </Button>
                    )}
                    {canDelete && (
                        <Button data-testid="btn-delete-role" variant="destructive" size="sm" onClick={deleteDialog.open}>
                            <Icon icon={Trash2} className="mr-2"/>
                            {i18n.common.delete}
                        </Button>
                    )}
                </>
            ) : undefined}
        >
            {/* Permissions Edit Actions */}
            {role && canUpdate && (
                <InlineRow justify="end">
                    {isEditingPermissions ? (
                        <>
                            <Button
                                data-testid="btn-cancel-edit-permissions"
                                variant="outline"
                                onClick={handleCancelEditingPermissions}
                                disabled={updatePermissionsMutation.isPending}
                            >
                                {i18n.common.cancel}
                            </Button>
                            <Button
                                data-testid="btn-save-permissions"
                                onClick={handleSavePermissions}
                                disabled={updatePermissionsMutation.isPending}
                            >
                                {updatePermissionsMutation.isPending ? i18n.common.saving : i18n.roles.savePermissions}
                            </Button>
                        </>
                    ) : (
                        <Button data-testid="btn-edit-permissions" variant="outline" onClick={handleStartEditingPermissions}>
                            <Icon icon={Settings} className="mr-2"/>
                            {i18n.roles.editPermissions}
                        </Button>
                    )}
                </InlineRow>
            )}

            {role && <>
            <CardSection title={i18n.roles.title} data-testid="role-meta-card">
                <FormGrid columns={2}>
                    <DetailField
                        label={i18n.roles.keyLabel}
                        value={
                            <CodeText>
                                <span data-testid="role-key-value">{role.key}</span>
                            </CodeText>
                        }
                    />
                    <DetailField
                        label={i18n.common.description}
                        value={<span data-testid="role-description-value">{role.description || '\u2014'}</span>}
                    />
                </FormGrid>
                <MutedText size="xs">{i18n.roles.keyHint}</MutedText>
            </CardSection>

            {/* Permissions Table */}
            <PermissionsMatrix
                rolePermissions={role.permissions}
                allPermissions={allPermissions}
                isEditing={isEditingPermissions}
                selectedPermissionIds={selectedPermissionIds}
                onTogglePermission={handleTogglePermission}
            />

            {/* Edit Dialog */}
            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.roles.editRole}</DialogTitle>
                        <DialogDescription>
                            {i18n.roles.editDescription}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => { e.preventDefault(); handleUpdate() }}>
                        <FormGrid>
                            <FormField label={i18n.common.name} htmlFor="edit-name">
                                <Input
                                    id="edit-name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder={i18n.roles.namePlaceholder}
                                />
                            </FormField>
                            <FormField label={i18n.common.description} htmlFor="edit-description">
                                <Input
                                    id="edit-description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder={i18n.roles.descriptionPlaceholder}
                                />
                            </FormField>
                        </FormGrid>
                        <SaveCancelFooter
                            testIdPrefix="edit-dialog"
                            onCancel={editDialog.close}
                            saving={updateMutation.isPending}
                            disabled={!formData.name}
                            saveLabel={i18n.common.saveChanges}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.roles.deleteRole}</DialogTitle>
                        <DialogDescription>
                            {i18n.roles.deleteConfirm({ name: role.name })}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete-dialog"
                        onCancel={deleteDialog.close}
                        saving={deleteMutation.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={handleDelete}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
